import React, { useContext, useState, useRef, useEffect } from 'react';
import { LanguageContext } from '../../context/LanguageContext';
import styles from './LanguageToggle.module.css';
import { useTranslation } from '../../hooks/useTranslation';

interface LanguageToggleProps {
  isWideScreenOnly?: boolean;
}

const LanguageToggle: React.FC<LanguageToggleProps> = ({ isWideScreenOnly = false }) => {
  const context = useContext(LanguageContext);
  const { t } = useTranslation();
  const [isFlipping, setIsFlipping] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const textRef = useRef<HTMLSpanElement>(null);

  if (!context) {
    throw new Error('LanguageToggle must be used within a LanguageProvider');
  }

  const { toggleLanguage, language } = context;

  const handleToggle = (e: React.MouseEvent<HTMLButtonElement>) => {
    // Add ripple effect
    const button = buttonRef.current;
    if (button) {
      const rect = button.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      
      const ripple = document.createElement('span');
      ripple.className = styles.ripple;
      ripple.style.left = `${x}px`;
      ripple.style.top = `${y}px`;
      
      button.appendChild(ripple);
      
      setTimeout(() => {
        ripple.remove();
      }, 600);
    }
    
    // Already flipping, don't trigger again
    if (isFlipping) return;
    
    // Start flip animation
    setIsFlipping(true);
    
    // Change language halfway through the flip
    setTimeout(() => {
      toggleLanguage();
    }, 250);
  };

  useEffect(() => {
    if (isFlipping) {
      const timer = setTimeout(() => {
        setIsFlipping(false);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [isFlipping]);

  return (
    <button 
      ref={buttonRef}
      className={`${styles.languageToggle} ${isWideScreenOnly ? styles.wideScreenOnly : ''}`}
      onClick={handleToggle}
      aria-label={`Switch to ${language === 'pl' ? 'English' : 'Polish'}`}
    >
      <span 
        ref={textRef}
        className={`${styles.textContainer} ${isFlipping ? styles.flipping : ''}`}
      >
        {t('language.toggle')}
      </span>
    </button>
  );
};

export default LanguageToggle; 